
import React from 'react';
import { Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { Crown, Star, Zap, ShieldCheck, Trophy, Target, TrendingUp, Award, Gem, Flame } from 'lucide-react';

interface KolLevelCardProps {
  points: number;
  language: Language;
}

// 10 Levels Configuration
const LEVELS = [
    { level: 1, minPoints: 0, icon: Target, nameKey: 'lvl_1', color: 'text-slate-400' }, // Novice
    { level: 2, minPoints: 500, icon: TrendingUp, nameKey: 'lvl_2', color: 'text-emerald-400' }, // Explorer
    { level: 3, minPoints: 1500, icon: Star, nameKey: 'lvl_3', color: 'text-blue-400' }, // Rising Star
    { level: 4, minPoints: 3000, icon: Zap, nameKey: 'lvl_4', color: 'text-yellow-400' }, // Influencer
    { level: 5, minPoints: 5000, icon: ShieldCheck, nameKey: 'lvl_5', color: 'text-indigo-400' }, // KOL
    { level: 6, minPoints: 10000, icon: Flame, nameKey: 'lvl_6', color: 'text-orange-500' }, // Trendsetter
    { level: 7, minPoints: 25000, icon: Gem, nameKey: 'lvl_7', color: 'text-purple-400' }, // Crypto Native
    { level: 8, minPoints: 50000, icon: Award, nameKey: 'lvl_8', color: 'text-pink-500' }, // Whale Whisperer
    { level: 9, minPoints: 100000, icon: Trophy, nameKey: 'lvl_9', color: 'text-amber-300' }, // Market Mover
    { level: 10, minPoints: 250000, icon: Crown, nameKey: 'lvl_10', color: 'text-yellow-500 drop-shadow-md' }, // Legend
];

export const KolLevelCard: React.FC<KolLevelCardProps> = ({ points, language }) => {
  const t = TRANSLATIONS[language];

  // Determine Current Level
  let currentLevelIdx = 0;
  for (let i = LEVELS.length - 1; i >= 0; i--) {
      if (points >= LEVELS[i].minPoints) {
          currentLevelIdx = i;
          break;
      }
  }
  
  const currentLevel = LEVELS[currentLevelIdx];
  const nextLevel = LEVELS[currentLevelIdx + 1];
  const LevelIcon = currentLevel.icon;

  // Calculate Progress
  let progressPercent = 100;
  let pointsNeeded = 0;

  if (nextLevel) {
      const range = nextLevel.minPoints - currentLevel.minPoints;
      const currentProgress = points - currentLevel.minPoints;
      progressPercent = (currentProgress / range) * 100;
      pointsNeeded = nextLevel.minPoints - points;
  }

  return (
    <div className="bg-gradient-to-r from-slate-800 to-slate-900 border border-slate-700 rounded-xl p-6 relative overflow-hidden">
        {/* Background glow based on level color */}
        <div className={`absolute top-0 right-0 w-64 h-64 opacity-5 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none bg-current ${currentLevel.color}`}></div>

        <div className="flex items-center gap-6 relative z-10">
            {/* Badge Icon */}
            <div className={`w-20 h-20 rounded-2xl bg-slate-900 border border-slate-700 flex items-center justify-center shadow-lg ${currentLevel.color}`}>
                <LevelIcon size={40} className="drop-shadow-lg" />
            </div>

            {/* Info */}
            <div className="flex-1">
                <div className="flex justify-between items-start">
                    <div>
                        <p className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">{t.currentLevel}</p>
                        <h2 className={`text-2xl font-extrabold text-white flex items-center gap-2`}>
                            {/* @ts-ignore */}
                            {t[currentLevel.nameKey]}
                            <span className="text-xs bg-slate-700 text-white px-2 py-0.5 rounded border border-slate-600">LVL {currentLevel.level}</span>
                        </h2>
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-slate-500 mb-1">{t.pointsBalance}</p>
                        <p className="text-xl font-bold text-indigo-400 font-mono">{points.toLocaleString()}</p>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                        {nextLevel ? (
                            <>
                                <span>{Math.floor(progressPercent)}% to LVL {nextLevel.level}</span>
                                <span>{pointsNeeded.toLocaleString()} {t.pointsToGo}</span>
                            </>
                        ) : (
                            <span className="text-amber-400 font-bold">Max Level Reached!</span>
                        )}
                    </div>
                    <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden border border-slate-600">
                        <div 
                            className={`h-full transition-all duration-1000 ease-out bg-current ${currentLevel.color.replace('text-', 'bg-')}`} 
                            style={{ width: `${progressPercent}%` }}
                        >
                            {/* Animated shimmer effect */}
                            <div className="w-full h-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        {/* Benefits Tooltip (Static for demo) */}
        <div className="mt-4 pt-4 border-t border-slate-700/50 flex items-center gap-2 text-xs text-slate-400">
            <Award size={14} className="text-indigo-400" />
            <span className="font-semibold text-indigo-300">{t.levelBenefits}:</span>
            <span>Fee discount, Priority Support, SBT Badge Minting</span>
        </div>
    </div>
  );
};
